import { filterActivePrices } from 'components/commercetools-ui/molecules/product-tile/helpers';
import { Category } from 'types/entity/category';
import { Money, ProductPrices, Variant } from 'types/entity/product';
import { discountTypeHelper } from './price-utils';

type MonthOption = {
  key: string;
  value: string;
};

export const clearSpaces = (value: string) => value.split(' ').join('');

export const getEstimationPhrase = (days: number) => {
  const estimatedDeliveryDate = new Date();

  estimatedDeliveryDate.setDate(estimatedDeliveryDate.getDate() + days);

  const formattedEstimatedDeliveryDate = estimatedDeliveryDate.toLocaleDateString().replace(/\//g, '-');

  const estimationPhrase = `Est. ${formattedEstimatedDeliveryDate} to`;

  return estimationPhrase;
};

export function findCatPath(
  allCategories: Category[],
  targetKey?: string,
  path: Category[] = [],
): Category[] | undefined {
  for (const cat of allCategories) {
    const newPath = [...path, cat];

    if (cat.categoryId === targetKey) return newPath;
    if (cat?.descendants?.length) {
      const result = findCatPath(cat?.descendants, targetKey, newPath);
      if (result) return result;
    }
  }
}

export const getVariantLabelHelper = (locale: string, attribute: string, attributesData?: Record<string, any>) => {
  const label = attributesData?.find((attr: { name: string }) => attr.name === attribute)?.label;
  const keys = Object.keys(label ?? {});
  locale = locale ?? 'en-US';
  const localeKey = keys?.find((k) => k.toLowerCase() === locale.toLowerCase());
  return localeKey && (label[localeKey] as string);
};

export function calculateHeight(px: number, viewportHeight?: number): number {
  const vhBase =
    typeof viewportHeight === 'number' ? viewportHeight : (globalThis.window?.innerHeight) || 0;

  if (!vhBase) return 0;
  return (px / vhBase) * 100;
}

export const appendMonthYearToUrl = (
  url: string,
  month?: MonthOption | null,
  year?: string | null,
  hasChannelKeys?: boolean,
): string => {
  if (!month?.key && !year && !hasChannelKeys) {
    return url;
  }

  const [path, queryString = ''] = url.split('?');
  const params = new URLSearchParams(queryString);

  if (month?.key) {
    params.set('month', month.key);
  }

  if (year?.trim()) {
    params.set('year', year);
  }
  if (hasChannelKeys) {
    params.set('isToggleP2r', 'true');
  }

  return `${path}?${params.toString()}`;
};

export function stripHtmlForSR(html: string) {
  if (!html) return '';
  return html
    ?.replace(/<li>/gi, ' ')
    ?.replace(/<\/li>/gi, '. ')
    ?.replace(/<\/p>/gi, '. ')
    ?.replace(/<br\s*\/?>/gi, '. ')
    ?.replace(/<[^>]+>/g, '')
    ?.replace(/\s+/g, ' ')
    ?.trim();
}

export const isValidMoney = (value?: ProductPrices): value is ProductPrices & { value: Money } => {
  if (!value) return false;
  const amount = Number(value?.value?.centAmount);
  return Number.isFinite(amount);
};

export const collectPricesOnly = (current?: Variant, p2rRetailerPrefixes?: string[]): ProductPrices[] => {
  if (!current || !Array.isArray(current.prices)) return [];
  const retailerCandidates = p2rRetailerPrefixes
    ? filterActivePrices(current.prices, p2rRetailerPrefixes)
    : current.prices;
  return retailerCandidates.filter(isValidMoney);
};

export const getLowestMoney = (list: ProductPrices[]): ProductPrices | undefined => {
  if (!Array.isArray(list) || list.length === 0) return undefined;
  return list.reduce<ProductPrices | undefined>((lowest, candidate) => {
    if (!isValidMoney(candidate)) return lowest;
    if (!lowest || !isValidMoney(lowest)) return candidate;
    const candidateAmount = Number(candidate.value.centAmount);
    const lowestAmount = Number(lowest.value.centAmount);
    return candidateAmount < lowestAmount ? candidate : lowest;
  }, undefined);
};

export const getDiscountedVal = (
  variant?: Variant,
  locale = 'en-US',
  channelBasedPriceUpdate?: ProductPrices,
  p2rRetailerPrefixes?: string[],
  fallbackLowestPrice?: ProductPrices,
) => {
  const lowestMoney = fallbackLowestPrice ?? getLowestMoney(collectPricesOnly(variant, p2rRetailerPrefixes));
  const conditionSelectedPrice = channelBasedPriceUpdate ?? lowestMoney;
  return discountTypeHelper(conditionSelectedPrice, locale);
};
